import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  History,
  CreditCard,
  Target,
  Edit2,
  Check,
  X,
  Flame,
  Plus,
  ChevronRight,
  BarChart3,
  HelpCircle,
  XCircle
} from 'lucide-react';

import { useInvestments } from '../hooks/useInvestments';
import { useLoans } from '../hooks/useLoans';
import { usePortfolio } from '../hooks/usePortfolio';
import { formatCurrency } from '../utils/helpers';
import { toast } from 'react-toastify';

// Format currency with abbreviation for large numbers
const formatCurrencyWithAbbreviation = (amount) => {
  const absAmount = Math.abs(amount);

  if (absAmount >= 1e12) {
    return `${amount < 0 ? '-' : ''}KES ${(absAmount / 1e12).toFixed(2)}T`;
  } else if (absAmount >= 1e9) {
    return `${amount < 0 ? '-' : ''}KES ${(absAmount / 1e9).toFixed(2)}B`;
  } else if (absAmount >= 1e6) {
    return `${amount < 0 ? '-' : ''}KES ${(absAmount / 1e6).toFixed(2)}M`;
  } else if (absAmount >= 1e3) {
    return `${amount < 0 ? '-' : ''}KES ${(absAmount / 1e3).toFixed(2)}K`;
  } else {
    return formatCurrency(amount); // your existing formatter
  }
};


// Tooltip Component
const Tooltip = ({ children, text, position = 'bottom' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const tooltipRef = React.useRef(null);

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newPosition = position;

      // Check if tooltip goes off screen and adjust
      if (position === 'right' && rect.right > viewportWidth) {
        newPosition = 'left';
      } else if (position === 'left' && rect.left < 0) {
        newPosition = 'right';
      } else if (position === 'bottom' && rect.bottom > viewportHeight) {
        newPosition = 'top';
      } else if (position === 'top' && rect.top < 0) {
        newPosition = 'bottom';
      }

      setAdjustedPosition(newPosition);
    }
  }, [isVisible, position]);

  const positionClasses = {
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        {children}
      </div>
      {isVisible && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 ${positionClasses[adjustedPosition]} w-48 sm:w-64 pointer-events-none`}
        >
          <div className="bg-[#2a3942] text-white text-xs rounded-lg px-3 py-2 shadow-lg border border-[#3d4f5c]">
            {text}
            <div className={`absolute w-2 h-2 bg-[#2a3942] border-[#3d4f5c] transform rotate-45
              ${adjustedPosition === 'bottom' ? 'border-t border-l -top-1 left-1/2 -translate-x-1/2' : ''}
              ${adjustedPosition === 'top' ? 'border-b border-r -bottom-1 left-1/2 -translate-x-1/2' : ''}
              ${adjustedPosition === 'left' ? 'border-r border-t -right-1 top-1/2 -translate-y-1/2' : ''}
              ${adjustedPosition === 'right' ? 'border-l border-b -left-1 top-1/2 -translate-y-1/2' : ''}
            `}></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Onboarding Tour Component
const OnboardingTour = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

const steps = [
{
  title: "Welcome to UtajiriHub 👋",
  description: "Do you know if you're making progress in life? Most people work hard but never measure their wealth. UtajiriHub helps you track everything you own and owe so you can see your real financial growth over time.",
  highlight: null
},
{
  title: "Set Your Wealth Goal 🎯",
  description: "Define what you want to achieve in the next 1, 5, or 10 years and track your progress as you move closer to financial freedom.",
  highlight: "goal"
},
{
  title: "Add Your Assets 💰",
  description: "Add what you own, such as savings, stocks, businesses, or real estate, to start building your wealth profile.",
  highlight: "assets"
},
{
  title: "Track Your Liabilities 💳",
  description: "Add what you owe, such as loans, debts, or mortgages, to get a complete picture of your financial position.",
  highlight: "liabilities"
},
{
  title: "Start Building Wealth 🚀",
  description: "Add your first asset, such as savings, or your first liability, such as a loan, and see your net worth instantly. Your data is completely private and secure.",
  highlight: null
}
];

  const currentStepData = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="bg-[#1f2c34] rounded-lg max-w-md w-full p-6 relative">
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-[#8696a0] hover:text-white transition-colors"
        >
          <XCircle size={24} />
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex gap-1">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === currentStep ? 'w-8 bg-[#00a884]' : 'w-1.5 bg-[#2a3942]'
                  }`}
                />
              ))}
            </div>
            <span className="text-[#8696a0] text-xs ml-2">
              {currentStep + 1} of {steps.length}
            </span>
          </div>

          <h3 className="text-white text-xl font-medium mb-2">
            {currentStepData.title}
          </h3>
          <p className="text-[#8696a0] text-sm leading-relaxed">
            {currentStepData.description}
          </p>
        </div>

        <div className="flex gap-3">
          {currentStep > 0 && (
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="flex-1 bg-[#2a3942] text-white px-4 py-3 rounded-lg hover:bg-[#364850] transition-colors"
            >
              Previous
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex-1 bg-[#00a884] text-white px-4 py-3 rounded-lg hover:bg-[#008f6d] transition-colors"
          >
            {currentStep < steps.length - 1 ? 'Next' : 'Get Started'}
          </button>
        </div>

        <button
          onClick={handleSkip}
          className="w-full text-[#8696a0] text-sm mt-3 hover:text-white transition-colors"
        >
          Skip tour
        </button>
      </div>
    </div>
  );
};

export default function Home() {
  const navigate = useNavigate();

  const {
    investments,
    loading: investmentsLoading,
    getBalanceByType
  } = useInvestments();

  const {
    loading: loansLoading,
  } = useLoans();

  const {
    summary,
    loading: settingsLoading,
    fetchPortfolioSummary,
    updateYearlyGoal
  } = usePortfolio();

  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    fetchPortfolioSummary();
  }, [fetchPortfolioSummary]);

  // Check if user should see the onboarding tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenWealthTrackerTour');
    
    // Show tour if user has never seen it AND data has finished loading
    if (!hasSeenTour && !investmentsLoading && !settingsLoading) {
      // Delay to ensure page is fully loaded
      setTimeout(() => setShowOnboarding(true), 500);
    }
  }, [investmentsLoading, settingsLoading]);

  const handleCompleteTour = () => {
    localStorage.setItem('hasSeenWealthTrackerTour', 'true');
    setShowOnboarding(false);
  };

  const yearlyGoal = summary?.yearlyGoal ?? 0;

  const getNetWorth = () => {
    return summary?.netWorth ?? 0;
  };

  const getTotalInvestments = () => {
    return summary?.totalInvestments ?? 0;
  };

  const getTotalLoansAmount = () => {
    return summary?.totalLoans ?? 0;
  };

  const startEditingGoal = () => {
    setGoalInput(yearlyGoal.toString() || '');
    setIsEditingGoal(true);
  };

  const saveGoal = async () => {
    const goal = parseFloat(goalInput);
    if (!isNaN(goal) && goal > 0) {
      const result = await updateYearlyGoal(goal);
      if (result.success) {
        setIsEditingGoal(false);
        fetchPortfolioSummary();
      } else {
        toast.error(result.error || 'Failed to update goal');
      }
    } else {
      toast.error('Please enter a valid number');
    }
  };

  const cancelEditingGoal = () => {
    setIsEditingGoal(false);
    setGoalInput('');
  };

  const getProgress = () => {
    if (summary?.goalProgress !== undefined) {
      return Math.max(0, Math.min(summary.goalProgress, 100));
    }
    if (!yearlyGoal || yearlyGoal === 0) return 0;
    return Math.max(0, Math.min((getNetWorth() / yearlyGoal) * 100, 100));
  };

  const typeBalances = getBalanceByType();

  if (investmentsLoading || loansLoading || settingsLoading) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center">
        <div className="text-gray-400 text-base">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b141a]">
      {showOnboarding && (
        <OnboardingTour
          onClose={() => setShowOnboarding(false)}
          onComplete={handleCompleteTour}
        />
      )}

      {/* Help Button - Fixed position with breathing animation */}
      <button
        onClick={() => setShowOnboarding(true)}
        className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-40 w-12 h-12 bg-[#00a884] rounded-full flex items-center justify-center shadow-lg hover:bg-[#008f6d] transition-colors animate-pulse"
        title="Show tour"
        style={{
          animation: 'breathe 2s ease-in-out infinite'
        }}
      >
        <HelpCircle className="w-6 h-6 text-white" />
      </button>

      {/* CSS for breathing animation */}
      <style>{`
        @keyframes breathe {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 10px 25px rgba(0, 168, 132, 0.3);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 15px 35px rgba(0, 168, 132, 0.5);
          }
        }
      `}</style>

      {/* Dev Tools - Hidden Reset Button (Double-click bottom-left corner) */}
      <div
        onDoubleClick={() => {
          localStorage.removeItem('hasSeenWealthTrackerTour');
          toast.success('Tour reset! Refresh to see it again.');
        }}
        className="fixed bottom-4 left-4 w-12 h-12 z-40 cursor-pointer opacity-0 hover:opacity-10"
        title="Double-click to reset tour"
      />
      

      <main className="max-w-7xl mx-auto pb-20 px-4 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-6 lg:mt-6">
          
          {/* Left Column - Net Worth & Goal */}
          <div className="space-y-4">
            {/* Net Worth Header */}
            <div className="bg-[#1f2c34] px-4 py-6 lg:px-6 lg:py-8 lg:rounded-lg relative" id="networth">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <p className="text-[#8696a0] text-sm lg:text-base font-normal">Net Worth</p>
                  <Tooltip text="Your total wealth: Assets minus Liabilities. This updates automatically as you add investments and loans." position="right">
                    <HelpCircle className="w-4 h-4 text-[#8696a0] hover:text-[#00a884] cursor-help" />
                  </Tooltip>
                </div>
                <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-[#00a884]" />
              </div>
              <p className="text-white text-3xl lg:text-5xl font-light mb-4 lg:mb-6 break-all">{formatCurrencyWithAbbreviation(getNetWorth())}</p>
              
              <div className="grid grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-[#2a3942] p-3 lg:p-4 rounded-lg">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-[#8696a0] text-xs lg:text-sm">Assets</span>
                    <Tooltip text="Total value of all your investments and assets" position="top">
                      <HelpCircle className="w-3 h-3 text-[#8696a0] hover:text-[#00a884] cursor-help" />
                    </Tooltip>
                  </div>
                  <span className="text-[#00a884] text-sm lg:text-xl font-medium break-all">{formatCurrencyWithAbbreviation(getTotalInvestments())}</span>
                </div>
                <div className="bg-[#2a3942] p-3 lg:p-4 rounded-lg">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-[#8696a0] text-xs lg:text-sm">Liabilities</span>
                    <Tooltip text="Total amount of all your loans and debts" position="top">
                      <HelpCircle className="w-3 h-3 text-[#8696a0] hover:text-[#00a884] cursor-help" />
                    </Tooltip>
                  </div>
                  <span className="text-[#ea4335] text-sm lg:text-xl font-medium break-all">{formatCurrencyWithAbbreviation(getTotalLoansAmount())}</span>
                </div>
              </div>
            </div>

            {/* Goal Section */}
            <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-6" id="goal">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#00a884]/10 flex items-center justify-center">
                    <Target className="w-5 h-5 lg:w-6 lg:h-6 text-[#00a884]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-base lg:text-lg font-normal">Wealth Goal</span>
                    <Tooltip text="Set a financial target and track your progress. We'll calculate how close you are to achieving it!" position="right">
                      <HelpCircle className="w-4 h-4 text-[#8696a0] hover:text-[#00a884] cursor-help" />
                    </Tooltip>
                  </div>
                </div>
                {!isEditingGoal && yearlyGoal > 0 && (
                  <Tooltip text="Click to update your goal" position="left">
                    <button onClick={startEditingGoal} className="text-[#00a884] p-1 hover:bg-[#2a3942] rounded transition-colors">
                      <Edit2 size={18} className="lg:w-5 lg:h-5" />
                    </button>
                  </Tooltip>
                )}
              </div>

              {isEditingGoal ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    className="flex-1 bg-[#2a3942] text-white px-3 py-2.5 lg:px-4 lg:py-3 rounded-lg border border-[#2a3942] focus:border-[#00a884] outline-none text-base lg:text-lg"
                    placeholder="Enter your goal"
                  />
                  <button onClick={saveGoal} className="bg-[#00a884] text-white w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center hover:bg-[#008f6d] transition-colors">
                    <Check size={18} className="lg:w-5 lg:h-5" />
                  </button>
                  <button onClick={cancelEditingGoal} className="bg-[#2a3942] text-white w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center hover:bg-[#1f2c34] transition-colors">
                    <X size={18} className="lg:w-5 lg:h-5" />
                  </button>
                </div>
              ) : (
                <>
                  {yearlyGoal > 0 ? (
                    <>
                      <div className="flex justify-between mb-3">
                        <span className="text-white text-base lg:text-2xl font-light break-all">{formatCurrencyWithAbbreviation(yearlyGoal)}</span>
                        <span className="text-[#8696a0] text-sm lg:text-base whitespace-nowrap ml-2">{getProgress().toFixed(0)}% achieved</span>
                      </div>
                      <div className="w-full bg-[#2a3942] h-2 lg:h-3 rounded-full overflow-hidden">
                        <div className="bg-[#00a884] h-full rounded-full transition-all duration-300"
                          style={{ width: `${getProgress()}%` }} />
                      </div>
                      {getProgress() < 100 ? (
                        <p className="text-[#8696a0] text-xs lg:text-sm mt-2 lg:mt-3">
                          {formatCurrencyWithAbbreviation(yearlyGoal - getNetWorth())} remaining
                        </p>
                      ) : (
                        <p className="text-[#00a884] text-xs lg:text-sm font-medium mt-2 lg:mt-3">
                          ✓ Goal achieved
                        </p>
                      )}
                    </>
                  ) : (
                    <button onClick={startEditingGoal}
                      className="w-full bg-[#2a3942] text-[#00a884] rounded-lg px-4 py-2.5 lg:py-3 text-sm lg:text-base font-medium hover:bg-[#364850] transition-colors">
                      Set your goal
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Balance by Investment Type - Desktop */}
            {investments.length > 0 && (
              <div className="hidden lg:block">
                <div className="flex items-center gap-2 px-4 mb-2">
                  <p className="text-[#8696a0] text-xs uppercase tracking-wide">Portfolio Breakdown</p>
                  <Tooltip text="See how your assets are distributed across different investment types" position="right">
                    <HelpCircle className="w-3 h-3 text-[#8696a0] hover:text-[#00a884] cursor-help" />
                  </Tooltip>
                </div>
                <div className="bg-[#1f2c34] rounded-lg p-6">
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(typeBalances).map(([type, balance]) => (
                      <div key={type} className="bg-[#0b141a] p-4 rounded-lg">
                        <p className="text-[#8696a0] text-sm mb-2 truncate">{type}</p>
                        <p className="text-[#00a884] font-medium text-lg">
                          {formatCurrencyWithAbbreviation(balance)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Quick Actions */}
          <div className="mt-6 lg:mt-0 space-y-6">
            <div>
              <p className="text-[#8696a0] text-xs uppercase tracking-wide px-4 mb-2 lg:text-sm">Quick Actions</p>
              
              <div className="bg-[#1f2c34] rounded-lg overflow-hidden divide-y divide-[#2a3942]">

                <button
                  onClick={() => navigate("/investments")}
                  className="w-full px-4 py-3.5 lg:px-6 lg:py-5 flex items-center gap-3 lg:gap-4 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors"
                  id="assets"
                >
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 lg:w-7 lg:h-7 text-emerald-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white text-base lg:text-lg font-normal">My Assets</p>
                    <p className="text-[#8696a0] text-sm lg:text-base">{summary?.investmentCount ?? 0} investments</p>
                  </div>
                  <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-[#8696a0]" />
                </button>

                <button
                  onClick={() => navigate("/loans")}
                  className="w-full px-4 py-3.5 lg:px-6 lg:py-5 flex items-center gap-3 lg:gap-4 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors"
                  id="liabilities"
                >
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 lg:w-7 lg:h-7 text-red-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white text-base lg:text-lg font-normal">My Liabilities</p>
                    <p className="text-[#8696a0] text-sm lg:text-base">{summary?.loanCount ?? 0} loans</p>
                  </div>
                  <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-[#8696a0]" />
                </button>

                <button
                  onClick={() => navigate("/statistics")}
                  className="w-full px-4 py-3.5 lg:px-6 lg:py-5 flex items-center gap-3 lg:gap-4 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors"
                >
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-6 h-6 lg:w-7 lg:h-7 text-purple-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white text-base lg:text-lg font-normal">Statistics</p>
                    <p className="text-[#8696a0] text-sm lg:text-base">View growth trends</p>
                  </div>
                  <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-[#8696a0]" />
                </button>

                <button
                  onClick={() => navigate("/marketplace")}
                  className="w-full px-4 py-3.5 lg:px-6 lg:py-5 flex items-center gap-3 lg:gap-4 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors"
                >
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                    <Flame className="w-6 h-6 lg:w-7 lg:h-7 text-orange-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white text-base lg:text-lg font-normal">Acquire Assets</p>
                    <p className="text-[#8696a0] text-sm lg:text-base">{summary?.opportunityDealsCount ?? 0} opportunities</p>
                  </div>
                  <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-[#8696a0]" />
                </button>

                <button
                  onClick={() => navigate("/transactions")}
                  className="w-full px-4 py-3.5 lg:px-6 lg:py-5 flex items-center gap-3 lg:gap-4 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors"
                >
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <History className="w-6 h-6 lg:w-7 lg:h-7 text-blue-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white text-base lg:text-lg font-normal">Transaction History</p>
                    <p className="text-[#8696a0] text-sm lg:text-base">View all activity</p>
                  </div>
                  <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-[#8696a0]" />
                </button>
              </div>
            </div>

            {/* Empty State */}
            {investments.length === 0 && (
              <div className="bg-[#1f2c34] rounded-lg p-8 lg:p-12 text-center">
                <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#00a884]/10 flex items-center justify-center mx-auto mb-4 lg:mb-6">
                  <TrendingUp className="w-8 h-8 lg:w-10 lg:h-10 text-[#00a884]" />
                </div>
                <h3 className="text-white text-lg lg:text-2xl font-normal mb-2 lg:mb-3">Start Building Wealth</h3>
                <p className="text-[#8696a0] text-sm lg:text-base mb-6 lg:mb-8 leading-relaxed">
                  Track your assets and liabilities in one simple place
                </p>
                <button
                  onClick={() => navigate("/investments")}
                  className="bg-[#00a884] text-white px-6 py-3 lg:px-8 lg:py-4 rounded-full inline-flex items-center gap-2 font-medium text-sm lg:text-base active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors">
                  <Plus size={18} className="lg:w-5 lg:h-5" />
                  Add First Asset
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Only - Balance by Investment Type */}
        {investments.length > 0 && (
          <div className="lg:hidden mt-6">
            <p className="text-[#8696a0] text-xs uppercase tracking-wide px-4 mb-2">Portfolio Breakdown</p>
            <div className="bg-[#1f2c34] rounded-lg p-4">
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(typeBalances).map(([type, balance]) => (
                  <div key={type} className="bg-[#0b141a] p-3 rounded-lg">
                    <p className="text-[#8696a0] text-xs mb-1 truncate">{type}</p>
                    <p className="text-[#00a884] font-medium text-sm">
                      {formatCurrencyWithAbbreviation(balance)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}